import { useState } from "react";
// eslint-disable-next-line import/named
import { FieldValues, useForm } from "react-hook-form";
import { useImmer } from "use-immer";
import { createPath } from "@/service/routing/routing";
import { trans } from "@/service/i18n/i18n";
import { model } from "@/components/security/login/model";
import { preSubmit } from "@/forms/validation";
import { handleUserError } from "@/service/client-api/error-handler";
import { Button } from "@/components/ui/button";
import { SuperLink as Link } from "@/components/common/super-link";
import { SecurityLink } from "@/components/security/security-link";
import { GoogleAuth } from "@/components/security/login/components/google-auth";
import { setGoogleConsentMode } from "@/service/logs/log";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { getCaptcha } from "@/forms/helpers";
import { useEnvContext } from "next-runtime-env";
import { PasswordField } from "@/forms/fields/password-field/password-field";
import { RecaptchaField } from "@/forms/fields/recaptcha-field";
import { TextField } from "@/forms/fields/text-field/text-field";
import { Form } from "@/components/ui/form";
import { LoaderIcon } from "lucide-react";
import { FormError } from "@/forms/form-error";
import { Separator } from "@/components/ui/separator";
import { AppleAuth } from "@/components/security/login/components/apple-auth/apple-auth";
import { QrAuth } from "@/components/security/login/components/qr-auth/qr-auth";
import { FieldsContainer } from "@/forms/fields-container";

export const LoginNeeded = ({ redirectTo }: { redirectTo?: string }) => {
  const [fields, setFields] = useImmer(model);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { NEXT_PUBLIC_RECAPTCHA_SITE_KEY } = useEnvContext();

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { handleSubmit } = form;

  const onSubmit = async ({ emailOrPwz, password }: FieldValues) => {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    try {
      const recaptcha = await getCaptcha(
        "error",
        NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string,
      );
      const response = await fetch("/api/login", {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ emailOrPwz, password, recaptcha }),
      });

      if (response.status !== 200) {
        throw await response.json();
      }

      const publicationArticle = document.querySelector(
        ".publication-article",
      ) as undefined | HTMLElement;

      if (
        publicationArticle &&
        !document.referrer.includes("https://remedium.md")
      ) {
        window.dataLayer?.push({
          event: "login_to_visit_article",
          is_logged_in: !!publicationArticle.dataset.isLoggedIn,
        } as never);
      }
      setGoogleConsentMode(true);

      if (redirectTo) {
        location.href = redirectTo;
      } else {
        location.reload();
      }
    } catch (caughtError) {
      setLoading(false);
      handleUserError(caughtError, setError, setFields);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h2
          className={`text-card-foreground mb-6 text-center text-2xl font-semibold`}
        >
          {trans("permissions.login.title")}
        </h2>
        <FieldsContainer>
          <TextField field={fields.emailOrPwz} />

          <PasswordField field={fields.password} />
        </FieldsContainer>

        <SecurityLink>
          <Link href={createPath("security.forgot_password")}>
            {trans("lost.password")}
          </Link>
        </SecurityLink>

        <Button type="submit" className={"mb-4 w-full"}>
          {loading && <LoaderIcon className={"animate-spin"} size={16} />}{" "}
          {trans("login.login")}
        </Button>

        <FormError error={error} />

        <div
          className={`text-muted-foreground my-6 flex h-2.5 w-full items-center justify-between text-xs font-normal`}
        >
          <Separator className={"w-[calc(50%-18px)]!"} />
          lub <Separator className={"w-[calc(50%-18px)]!"} />
        </div>

        <GoogleAuth />

        <AppleAuth />
        <QrAuth />

        <div className={`text-foreground my-6 text-center text-sm font-normal`}>
          Nie masz konta?{" "}
          <Link
            className={"text-remedium-blue"}
            href={createPath("security.registration")}
          >
            {trans("register")}
          </Link>
        </div>

        <RecaptchaField />
      </form>
    </Form>
  );
};
